export interface LinkedInAccount {
  connection_id: string;
  member_id: string;
  display_name: string;
  email?: string;
  picture_url?: string;
  profile_url?: string;
  scopes?: string[];
  status?: 'connected' | 'expired' | 'revoked' | 'error';
  connected_at?: string;
  expires_at?: string | null;
}

export interface LinkedInAccountsPayload {
  connected?: boolean;
  accounts?: LinkedInAccount[];
}

interface LinkedInBackendAccount {
  connection_id?: string;
  id?: string;
  member_id?: string;
  linkedin_member_id?: string;
  display_name?: string;
  name?: string;
  email?: string;
  picture_url?: string;
  picture?: string | null;
  profile_url?: string;
  scopes?: string[];
  status?: LinkedInAccount['status'];
  connected_at?: string;
  expires_at?: string | null;
}

async function parseResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error('LinkedIn connector is not available on this deployment yet.');
  }
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const value = payload as { message?: string; error?: string };
    throw new Error(value.message || value.error || 'LinkedIn request failed.');
  }
  return payload as T;
}

async function linkedinRequest<T>(path: string, workspaceId: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('X-Nexa-Workspace', workspaceId);
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  const response = await fetch(path, { ...init, headers, credentials: 'same-origin' });
  return parseResponse<T>(response);
}

function normalizeAccount(account: LinkedInBackendAccount): LinkedInAccount {
  const expiresAt = account.expires_at || null;
  const expired = Boolean(expiresAt && Date.parse(expiresAt) <= Date.now());
  return {
    connection_id: String(account.connection_id || account.id || ''),
    member_id: String(account.member_id || account.linkedin_member_id || ''),
    display_name: String(account.display_name || account.name || account.email || 'LinkedIn account'),
    email: account.email,
    picture_url: account.picture_url || account.picture || undefined,
    profile_url: account.profile_url,
    scopes: account.scopes,
    status: account.status || (expired ? 'expired' : 'connected'),
    connected_at: account.connected_at,
    expires_at: expiresAt,
  };
}

export const linkedinApi = {
  list: async (workspaceId: string): Promise<LinkedInAccountsPayload> => {
    const payload = await linkedinRequest<{ ok?: boolean; connected?: boolean; accounts?: LinkedInBackendAccount[] }>('/api/linkedin/accounts', workspaceId);
    return { connected: payload.connected, accounts: (payload.accounts || []).map(normalizeAccount) };
  },
  connectUrl: (workspaceId: string, returnView: 'accounts' | 'planner' = 'accounts') => {
    const returnUrl = new URL(`${window.location.origin}/`);
    returnUrl.searchParams.set('view', returnView);
    returnUrl.searchParams.set('workspace', workspaceId);
    const params = new URLSearchParams({ workspace_id: workspaceId, return_to: returnUrl.toString() });
    return `/auth/linkedin/start?${params.toString()}`;
  },
  disconnect: (workspaceId: string, connectionId: string) => linkedinRequest<{ ok?: boolean }>(`/api/linkedin/disconnect/${encodeURIComponent(connectionId)}`, workspaceId, { method: 'POST' }),
  refresh: (workspaceId: string, connectionId: string) => linkedinRequest<{ ok?: boolean }>(`/api/linkedin/refresh/${encodeURIComponent(connectionId)}`, workspaceId, { method: 'POST' }),
};

export interface LinkedInPublishingSettings {
  visibility: 'PUBLIC' | 'CONNECTIONS';
  disable_comments: boolean;
}

export const defaultLinkedInPublishingSettings: LinkedInPublishingSettings = {
  visibility: 'PUBLIC',
  disable_comments: false,
};
